/**
 * LACUNA 0 THEME - JAVASCRIPT
 * Core client-side interactions: cart drawer, mobile navigation, image galleries, and layout effects.
 */

(function () {
  'use strict';

  // --- THEME STATE & HELPERS ---
  const themeState = {
    cartOpen: false,
    menuOpen: false,
  };

  const selectors = {
    header: '#SiteHeader',
    cartDrawer: '#CartDrawer',
    cartDrawerTrigger: '.js-cart-drawer-trigger',
    cartDrawerClose: '.js-cart-drawer-close',
    cartDrawerItems: '.js-cart-drawer-items',
    cartCount: '.js-cart-count',
    cartSubtotal: '.js-cart-subtotal',
    shippingThreshold: '.js-shipping-threshold',
    mobileMenu: '#MobileMenu',
    mobileMenuTrigger: '.js-mobile-menu-trigger',
    galleryMain: '.js-gallery-main',
    galleryThumb: '.js-gallery-thumb',
    variantSelector: '.js-variant-selector',
    productPrice: '.js-product-price',
    addToCartForm: '.js-add-to-cart-form',
    tabItem: '.js-tab-item',
    tabHeader: '.js-tab-header',
    fadeInSection: '.fade-in-section',
    quantityBtn: '.js-quantity-btn',
    quantityInput: '.js-quantity-input',
  };

  /**
   * Safe DOM helper to add/remove classes or fire actions
   */
  function qs(selector, scope = document) {
    return scope.querySelector(selector);
  }

  function qsa(selector, scope = document) {
    return Array.from(scope.querySelectorAll(selector));
  }

  // Formatting Money using Shopify's configuration fallback
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || window.theme.moneyFormat || '$ {{amount}}';

    function defaultOption(opt, def) {
      return typeof opt == 'undefined' ? def : opt;
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal = defaultOption(decimal, '.');

      if (isNaN(number) || number == null) return 0;

      number = (number / 100.0).toFixed(precision);

      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? decimal + parts[1] : '';

      return dollarsAmount + centsAmount;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
    }

    return formatString.replace(placeholderRegex, value);
  }

  // --- INITIALIZATION ---
  document.addEventListener('DOMContentLoaded', () => {
    initStickyHeader();
    initCartDrawer();
    initMobileMenu();
    initProductGallery();
    initVariantSelectors();
    initProductTabs();
    initQuantitySelectors();
    initScrollAnimations();
    initAjaxCartHandler();
  });

  // --- 1. STICKY HEADER SHRINK EFFECT ---
  function initStickyHeader() {
    const header = qs(selectors.header);
    if (!header) return;

    window.addEventListener('scroll', () => {
      if (window.scrollY > 50) {
        header.classList.add('site-header--shrink');
      } else {
        header.classList.remove('site-header--shrink');
      }
    });
  }

  // --- 2. CART DRAWER TOGGLE & SYSTEM ---
  function initCartDrawer() {
    const drawer = qs(selectors.cartDrawer);
    const triggers = qsa(selectors.cartDrawerTrigger);
    const closeBtns = qsa(selectors.cartDrawerClose);

    if (!drawer) return;

    function openCart() {
      drawer.setAttribute('aria-hidden', 'false');
      triggers.forEach(t => t.setAttribute('aria-expanded', 'true'));
      document.body.style.overflow = 'hidden'; // Lock background scroll
      themeState.cartOpen = true;
      fetchAndRenderCart();
    }

    function closeCart() {
      drawer.setAttribute('aria-hidden', 'true');
      triggers.forEach(t => t.setAttribute('aria-expanded', 'false'));
      document.body.style.overflow = '';
      themeState.cartOpen = false;
    }

    // Event listeners
    triggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        openCart();
      });
    });

    closeBtns.forEach(btn => {
      btn.addEventListener('click', closeCart);
    });

    // Close on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && themeState.cartOpen) {
        closeCart();
      }
    });

    // Expose drawer controls globally so other parts can open it
    window.theme.openCartDrawer = openCart;
    window.theme.closeCartDrawer = closeCart;
    window.theme.refreshCart = fetchAndRenderCart;
  }

  // --- 3. MOBILE MENU TOGGLE ---
  function initMobileMenu() {
    const menu = qs(selectors.mobileMenu);
    const trigger = qs(selectors.mobileMenuTrigger);

    if (!menu || !trigger) return;

    function toggleMenu() {
      const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', !isExpanded);
      menu.setAttribute('aria-hidden', isExpanded);
      document.body.style.overflow = isExpanded ? '' : 'hidden';
      themeState.menuOpen = !isExpanded;
    }

    trigger.addEventListener('click', toggleMenu);
  }

  // --- 4. AJAX CART ACTIONS (RENDER & CHANGE) ---
  function fetchAndRenderCart() {
    const cartContainer = qs(selectors.cartDrawerItems);
    if (!cartContainer) return;

    fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        updateCartBadges(cart.item_count);
        renderCartItems(cart);
        updateCartTotals(cart);
      })
      .catch(err => console.error('Error fetching cart data:', err));
  }

  function updateCartBadges(count) {
    const badges = qsa(selectors.cartCount);
    badges.forEach(badge => {
      badge.textContent = count;
      badge.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  function renderCartItems(cart) {
    const container = qs(selectors.cartDrawerItems);
    if (!container) return;

    if (cart.item_count === 0) {
      container.innerHTML = `
        <div class="cart-drawer__empty-state">
          <p class="font-serif" style="text-align: center; margin-top: 3rem;">${window.theme.strings.cartEmpty}</p>
          <a href="/collections/all" class="button button--secondary button--full" style="margin-top: 2rem;">CONTINUE SHOPPING</a>
        </div>
      `;
      return;
    }

    let html = '';
    cart.items.forEach((item, index) => {
      const optionsStr = item.options_with_values && item.options_with_values.length > 0
        ? item.options_with_values.map(opt => `${opt.name}: ${opt.value}`).join(' / ')
        : '';

      html += `
        <div class="cart-item" data-line-key="${item.key}" data-index="${index + 1}">
          <div class="cart-item__image-wrapper">
            <img class="cart-item__image" src="${item.image ? item.image : '//cdn.shopify.com/s/images/admin/no-image-large.gif'}" alt="${item.product_title}" loading="lazy">
          </div>
          <div class="cart-item__details">
            <div>
              <h4 class="cart-item__title">${item.product_title}</h4>
              ${optionsStr ? `<span class="cart-item__options">${optionsStr}</span>` : ''}
            </div>
            <div class="cart-item__bottom">
              <div class="quantity-selector">
                <button type="button" class="quantity-btn js-cart-qty-btn" data-action="minus" data-key="${item.key}" data-qty="${item.quantity}">-</button>
                <input type="number" class="quantity-input js-cart-qty-input" data-key="${item.key}" value="${item.quantity}" min="0">
                <button type="button" class="quantity-btn js-cart-qty-btn" data-action="plus" data-key="${item.key}" data-qty="${item.quantity}">+</button>
              </div>
              <span class="cart-item__price">${formatMoney(item.final_line_price)}</span>
            </div>
            <div style="text-align: right; margin-top: 0.25rem;">
              <button type="button" class="cart-item__remove js-cart-remove" data-key="${item.key}">REMOVE</button>
            </div>
          </div>
        </div>
      `;
    });

    container.innerHTML = html;
    initCartEventDelegation();
  }

  function updateCartTotals(cart) {
    const subtotalEl = qs(selectors.cartSubtotal);
    if (subtotalEl) {
      subtotalEl.textContent = formatMoney(cart.total_price);
    }

    // Dynamic Free Shipping threshold calculations
    const thresholdEl = qs(selectors.shippingThreshold);
    if (thresholdEl) {
      const thresholdVal = parseFloat(thresholdEl.dataset.threshold); // Target amount in cents
      if (!isNaN(thresholdVal)) {
        const remaining = thresholdVal - cart.total_price;
        if (remaining > 0) {
          const percent = Math.min((cart.total_price / thresholdVal) * 100, 100);
          thresholdEl.innerHTML = `
            <p>SPEND ${formatMoney(remaining)} MORE TO SECURE FREE SHIPPING</p>
            <div class="shipping-threshold-bar">
              <div class="shipping-threshold-progress" style="width: ${percent}%;"></div>
            </div>
          `;
        } else {
          thresholdEl.innerHTML = `
            <p style="color: var(--color-white); font-weight: 500;">YOU'VE UNLOCKED FREE STANDARD SHIPPING!</p>
            <div class="shipping-threshold-bar">
              <div class="shipping-threshold-progress" style="width: 100%;"></div>
            </div>
          `;
        }
      }
    }
  }

  function initCartEventDelegation() {
    const container = qs(selectors.cartDrawerItems);
    if (!container) return;

    // Handle quantity and remove events dynamically inside the drawer
    container.addEventListener('click', (e) => {
      const target = e.target;

      // Handle item removal
      if (target.classList.contains('js-cart-remove')) {
        const key = target.dataset.key;
        updateCartQuantity(key, 0);
        return;
      }

      // Handle quantity click buttons
      if (target.classList.contains('js-cart-qty-btn')) {
        const key = target.dataset.key;
        const currentQty = parseInt(target.dataset.qty);
        const action = target.dataset.action;
        let nextQty = action === 'plus' ? currentQty + 1 : currentQty - 1;
        if (nextQty < 0) nextQty = 0;
        updateCartQuantity(key, nextQty);
      }
    });

    // Handle manual manual quantity input changes
    container.addEventListener('change', (e) => {
      const target = e.target;
      if (target.classList.contains('js-cart-qty-input')) {
        const key = target.dataset.key;
        const nextQty = parseInt(target.value);
        if (!isNaN(nextQty) && nextQty >= 0) {
          updateCartQuantity(key, nextQty);
        }
      }
    });
  }

  function updateCartQuantity(key, qty) {
    const payload = {
      id: key,
      quantity: qty
    };

    fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    })
      .then(res => res.json())
      .then(cart => {
        updateCartBadges(cart.item_count);
        renderCartItems(cart);
        updateCartTotals(cart);
      })
      .catch(err => console.error('Error changing item quantity:', err));
  }

  // --- 5. AJAX ADD-TO-CART (AJAX FORM INTERCEPTION) ---
  function initAjaxCartHandler() {
    document.addEventListener('submit', (e) => {
      // Find out if this form belongs to product additions
      const form = e.target.closest('form[action$="/cart/add"]');
      if (!form) return;

      e.preventDefault();

      const formData = new FormData(form);

      fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
        .then(res => {
          if (!res.ok) {
            throw new Error('Adding to cart failed. Stock limit reached or variation sold out.');
          }
          return res.json();
        })
        .then(item => {
          // Add animation/feedback if preferred, then reload drawer
          if (window.theme.openCartDrawer) {
            window.theme.openCartDrawer();
          }
        })
        .catch(err => {
          alert(err.message || 'An error occurred. Product could not be added to your bag.');
        });
    });
  }

  // --- 6. PRODUCT GALLERY THUMBNAIL NAV ---
  function initProductGallery() {
    const mainImg = qs(selectors.galleryMain);
    const thumbs = qsa(selectors.galleryThumb);

    if (!mainImg || thumbs.length === 0) return;

    thumbs.forEach(thumb => {
      thumb.addEventListener('click', (e) => {
        e.preventDefault();
        const highResUrl = thumb.dataset.src;
        if (!highResUrl) return;

        // Swap out primary source
        mainImg.src = highResUrl;

        // Manage active classes
        thumbs.forEach(t => t.classList.remove('product-gallery__thumbnail--active'));
        thumb.classList.add('product-gallery__thumbnail--active');
      });
    });
  }

  // --- 7. VARIANT SELECTOR INTERACTIVITY ---
  function initVariantSelectors() {
    const selectorsList = qsa(selectors.variantSelector);
    if (selectorsList.length === 0) return;

    selectorsList.forEach(select => {
      select.addEventListener('change', (e) => {
        const option = e.target.options[e.target.selectedIndex];
        const price = option.dataset.price;
        const comparePrice = option.dataset.comparePrice;
        const available = option.dataset.available === 'true';

        // Update single-product detail page price indicator safely
        const priceContainer = qs(selectors.productPrice);
        if (priceContainer) {
          let priceHtml = '';
          if (comparePrice && parseFloat(comparePrice) > parseFloat(price)) {
            priceHtml = `
              <span class="product-info__price product-info__price--sale">${formatMoney(price)}</span>
              <span class="product-info__price--compare">${formatMoney(comparePrice)}</span>
            `;
          } else {
            priceHtml = `<span class="product-info__price">${formatMoney(price)}</span>`;
          }
          priceContainer.innerHTML = priceHtml;
        }

        // Manage state of CTA (Add to bag)
        const submitBtn = qs('button[type="submit"]', e.target.closest('form'));
        if (submitBtn) {
          if (available) {
            submitBtn.disabled = false;
            submitBtn.textContent = window.theme.strings.addToCart;
          } else {
            submitBtn.disabled = true;
            submitBtn.textContent = window.theme.strings.soldOut;
          }
        }
      });
    });
  }

  // --- 8. QUANTITY CONTROLLER ---
  function initQuantitySelectors() {
    // Select selectors outside the cart (e.g. PDP page)
    const pdpSelectors = qsa('.product-form .quantity-selector');
    if (pdpSelectors.length === 0) return;

    pdpSelectors.forEach(container => {
      const input = qs(selectors.quantityInput, container);
      const btns = qsa(selectors.quantityBtn, container);

      if (!input) return;

      btns.forEach(btn => {
        btn.addEventListener('click', () => {
          const currentVal = parseInt(input.value) || 1;
          const action = btn.dataset.action;
          let newVal = action === 'plus' ? currentVal + 1 : currentVal - 1;
          if (newVal < 1) newVal = 1;
          input.value = newVal;
        });
      });
    });
  }

  // --- 9. PRODUCT ACCORDION TABS ---
  function initProductTabs() {
    const tabHeaders = qsa(selectors.tabHeader);
    if (tabHeaders.length === 0) return;

    tabHeaders.forEach(header => {
      header.addEventListener('click', (e) => {
        e.preventDefault();
        const parent = header.closest(selectors.tabItem);
        if (!parent) return;

        const isCurrentlyActive = parent.classList.contains('active');

        // Toggle state
        if (isCurrentlyActive) {
          parent.classList.remove('active');
          header.setAttribute('aria-expanded', 'false');
        } else {
          parent.classList.add('active');
          header.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  // --- 10. SCROLL ANIMATION (INTERSECTION OBSERVER) ---
  function initScrollAnimations() {
    if (!('IntersectionObserver' in window)) return;

    const sections = qsa(selectors.fadeInSection);
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target); // Trigger only once
        }
      });
    }, observerOptions);

    sections.forEach(section => {
      observer.observe(section);
    });
  }

})();
